import { useEffect, useState } from "react";
import { FiCheck, FiSearch, FiX } from "react-icons/fi";
import { useTranslation } from "../context/TranslationContext";
import { searchGroupCandidates } from "../api/messages";
import { getProfileAvatarUrl, getProfileInitial } from "../utils/userDisplay";
import { personName } from "../utils/groupChats";

const idOf = (value) => String(value?._id ?? value ?? "");

function Avatar({ user, size = "h-9 w-9" }) {
  const url = getProfileAvatarUrl(user);
  return url ? (
    <img src={url} alt="" className={`${size} shrink-0 rounded-full object-cover`} />
  ) : (
    <span className={`${size} grid shrink-0 place-items-center rounded-full bg-accent-default text-footnote-semibold text-accent-on`}>
      {getProfileInitial(user)}
    </span>
  );
}

/**
 * Pick people for a group: search by name, or choose from the people you chat with
 * (shown before anything is typed). `selected` are the people picked so far; `exclude`
 * ids — the group's members — can't be picked again.
 */
const GroupMembersPicker = ({ selected, onChange, exclude = [], isDark, autoFocus = false }) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const res = await searchGroupCandidates(query.trim());
        if (!cancelled) setResults(res.data || []);
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, query.trim() ? 250 : 0);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query]);

  const picked = (user) => selected.some((s) => idOf(s) === idOf(user));
  const toggle = (user) =>
    onChange(picked(user) ? selected.filter((s) => idOf(s) !== idOf(user)) : [...selected, user]);
  const shown = results.filter((u) => !exclude.includes(idOf(u)));

  const muted = "text-label-secondary";
  const base = "text-label-primary";

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* One line of a fixed height, so picking someone never moves the list under the pointer. */}
      <div className="flex h-9 shrink-0 items-center gap-1.5 overflow-x-auto px-4 pb-2 [scrollbar-width:none]">
        {selected.length === 0 && <span className={`text-footnote ${muted}`}>{t("groups.noneSelected")}</span>}
        {selected.map((user) => (
          <span
            key={idOf(user)}
            className={`inline-flex shrink-0 items-center gap-1.5 rounded-full py-1 pl-1 pr-2 text-footnote-semibold ${
              isDark ? "bg-accent-tinted text-accent-text" : "bg-accent-tinted text-status-success"
            }`}
          >
            <Avatar user={user} size="h-5 w-5" />
            {personName(user, t)}
            <button type="button" onClick={() => toggle(user)} aria-label={t("chatFiles.remove")} className="opacity-70 hover:opacity-100">
              <FiX className="h-3.5 w-3.5" />
            </button>
          </span>
        ))}
      </div>
      <div className="px-4 pb-2">
        <label
          className={`flex items-center gap-2 rounded-radius-sm border px-3 py-2 ${
            "border-separator-default bg-bg-raised"
          }`}
        >
          <FiSearch className={`h-4 w-4 shrink-0 ${muted}`} />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("groups.searchPeople")}
            autoFocus={autoFocus}
            className={`min-w-0 flex-1 bg-transparent text-subheadline outline-none ${base} placeholder-label-tertiary`}
          />
        </label>
      </div>
      <p className={`px-4 pb-1 text-caption-1-semibold uppercase tracking-wider ${muted}`}>
        {query.trim() ? t("groups.results") : t("groups.yourChats")}
      </p>
      <div className="min-h-[8rem] flex-1 overflow-y-auto overscroll-contain pb-2" data-scroll-lock-target>
        {shown.map((user) => {
          const on = picked(user);
          return (
            <button
              key={idOf(user)}
              type="button"
              onClick={() => toggle(user)}
              aria-pressed={on}
              className={`flex w-full items-center gap-3 px-4 py-2 text-left transition hover:bg-fill-quaternary`}
            >
              <Avatar user={user} />
              <span className="min-w-0 flex-1">
                <span className={`block truncate text-subheadline-semibold ${base}`}>{personName(user, t)}</span>
                {user.businessName && user.username && <span className={`block truncate text-footnote ${muted}`}>{user.businessName}</span>}
              </span>
              <span
                className={`grid h-5 w-5 shrink-0 place-items-center rounded-full border ${
                  on ? "border-accent-default bg-accent-default text-accent-on" : "border-separator-opaque"
                }`}
              >
                {on && <FiCheck className="h-3.5 w-3.5" />}
              </span>
            </button>
          );
        })}
        {!loading && shown.length === 0 && (
          <p className={`px-4 py-6 text-center text-subheadline ${muted}`}>{query.trim() ? t("groups.noOneFound") : t("groups.searchHint")}</p>
        )}
      </div>
    </div>
  );
};

export default GroupMembersPicker;
